import type { PaymentMethod } from '@/types/checkout'

/** What `POST /api/checkout` answers, as far as the checkout reads it. */
export interface CheckoutResponse {
  purchaseId?: string
  /** The option the order is for; on a resumed open payment it may be another option of the plan. */
  productId?: string
  orderId?: string | null
  productTitle?: string
  amount?: string
  currency?: string
  paymentMethod?: PaymentMethod | 'FREE'
  /** F-74: a free claim answers `free: true`. */
  free?: boolean
  thankYouMessage?: string | null
  installments?: number
  /** The provider's order status: `pending`, `paid`, `failed`, `canceled`… */
  status?: string | null
  checkoutUrl?: string | null
  qrCode?: string | null
  pixCode?: string | null
  boletoUrl?: string | null
  boletoBarcode?: string | null
  expiresAt?: string | null
}

export type CheckoutOutcome = (
  | { kind: 'pix'; purchaseId: string | null; pixCode: string; qrCodeUrl: string | null; expiresAt: string | null }
  | { kind: 'boleto'; purchaseId: string | null; boletoUrl: string | null; barcode: string | null }
  | { kind: 'paid'; purchaseId: string | null }
  /** F-74: a free product was claimed; nothing was charged. */
  | { kind: 'free'; purchaseId: string | null }
  | { kind: 'processing'; purchaseId: string | null }
  | { kind: 'refused'; purchaseId: string | null }
) & {
  /** Set when the order is not for the option on screen (a resumed open payment). */
  productId?: string | null
}

const REFUSED_STATUSES = ['failed', 'canceled', 'cancelled', 'not_authorized', 'refused']

/**
 * What actually happened, read from the body rather than the status code.
 *
 * The API answers 201 "purchase created" even when the provider refused the
 * order on the spot (BUGS.md FIN-29): the body then carries `status: "failed"`
 * and no PIX code. Trusting the 201 would show a QR code that does not exist,
 * or a success screen for a charge that never happened. So a PIX without a
 * code, or a boleto without a link, counts as refused whatever the status says.
 */
export const interpretCheckoutResponse = (
  response: CheckoutResponse | null | undefined,
  method: PaymentMethod | null
): CheckoutOutcome => {
  const purchaseId = response?.purchaseId ?? null
  const status = String(response?.status || '').toLowerCase()

  if (!response || REFUSED_STATUSES.includes(status)) return { kind: 'refused', purchaseId }

  // F-74: a free claim has no method and nothing to wait for.
  if (response.free === true) return { kind: 'free', purchaseId }

  if (method === 'PIX') {
    if (!response.pixCode) return { kind: 'refused', purchaseId }
    return {
      kind: 'pix',
      purchaseId,
      pixCode: response.pixCode,
      qrCodeUrl: response.qrCode || null,
      expiresAt: response.expiresAt || null
    }
  }

  if (method === 'BOLETO') {
    if (!response.boletoUrl && !response.boletoBarcode) return { kind: 'refused', purchaseId }
    return { kind: 'boleto', purchaseId, boletoUrl: response.boletoUrl || null, barcode: response.boletoBarcode || null }
  }

  if (status === 'paid') return { kind: 'paid', purchaseId }
  return { kind: 'processing', purchaseId }
}

interface ApiErrorLike {
  response?: {
    status?: number
    data?: { message?: string; errors?: { message?: string; param?: string; path?: string }[]; data?: unknown }
  }
}

/**
 * The message to show for a rejected request, from the API's `errors[]`.
 *
 * The API translates its messages (it gets `Accept-Language`), so they can be
 * shown as they come. A 500 carries a generic one; the caller falls back to
 * its own text when there is nothing usable.
 */
export const apiErrorMessage = (
  error: unknown
): { message: string | null; param: string | null; status: number | null; data: unknown } => {
  const data = (error as ApiErrorLike | null)?.response?.data
  const first = Array.isArray(data?.errors) ? data.errors[0] : null
  return {
    message: first?.message || data?.message || null,
    param: first?.param || first?.path || null,
    status: (error as ApiErrorLike | null)?.response?.status ?? null,
    /** The body's `data`, when a refusal carries what to do next (e.g. the 409 on a plan already held). */
    data: data?.data ?? null
  }
}
